import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import {
  Dialog,
  DialogSize,
} from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import {
  Icon,
  IconColor,
  IconSize,
} from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import { Link } from '@delmonicos/shared-design-system-react/src/components/link/link';
import { GeoMap } from '@delmonicos/shared-design-system-react/src/components/location/geo-map/geo-map.component';
import { createGoogleLatLngLiteralFromGeoLocation } from '@delmonicos/shared-design-system-react/src/components/location/geolocation';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { MiniTitle } from '@delmonicos/shared-design-system-react/src/components/typography/mini-title/mini-title';
import {
  Text,
  TextColor,
  TextFontSize,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { hasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { APP_PATH } from '@app/config/app-paths';
import { permissions } from '@app/core/authentication/authentication.slice';
import { ConfigurationService } from '@app/core/configuration/configuration.service';
import { useAppSelector } from '@app/core/hooks';
import { useChargePointContext } from '@app/feature/charge-point/charge-point.context';
import { useCpmsBoGetLocationQuery } from '@service/cpms-bo-location.service';

import { ChargePointLocationUpdateController } from '../../../../charge-point-location-update/charge-point-location-update.controller';
import * as css from '../../../charge-point-details.module.scss';

export const LocalizationCardComponent = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { location, network } = useChargePointContext();

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [actionUpdateIsDialogOpen, setActionUpdateIsDialogOpen] =
    useState(false);

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  const canUpdateLocation = useMemo(
    () =>
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.CHARGE_POINTS,
        action: ActionsByResource.CHARGE_POINTS.SET_LOCATION,
      }),
    [userPermissions],
  );

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const { data: locationFull } = useCpmsBoGetLocationQuery(location?.id ?? '', {
    skip: !location?.id,
  });

  const apiKey = ConfigurationService.getGoogleApiKey();

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <>
      <Card className={css.localizationCard} gap={GapEnum.M} flex={1}>
        <Stack gap={GapEnum.M} alignItems="center">
          <Stack flex={1}>
            <AccentTitle
              as={HeadingEnum.H2}
              icon="marker-pin-04"
              text={t('$.charge-point-details.localization.title')}
              variant={AccentVariantEnum.Primary}
            />
          </Stack>
          {canUpdateLocation ? (
            <ButtonIcon
              onClick={() => setActionUpdateIsDialogOpen(true)}
              icon="edit-05"
              key="update-location"
            />
          ) : (
            <span />
          )}
        </Stack>
        <Stack direction="column" gap={GapEnum.M}>
          <Stack alignItems="center" justifyContent="space-between">
            <Stack alignItems="center" flex={1} gap={GapEnum.XS}>
              <Icon
                iconSize={IconSize.Small}
                color={IconColor.Secondary}
                name="server-06"
              />
              <MiniTitle color={TextColor.Secondary}>
                {t(
                  'delmonicos-shared-sdk.outputs-gateway-charge-point.network',
                )}
              </MiniTitle>
            </Stack>
            <Stack
              flex={1}
              gap={GapEnum.M}
              direction="row"
              alignItems="center"
              justifyContent="stretch">
              {network ? (
                <Link
                  onClick={() =>
                    navigate(
                      generatePath(APP_PATH.NETWORK.DETAILS, {
                        id: network.id,
                      }),
                    )
                  }>
                  <Text
                    className={css.linkUnderlined}
                    fontSize={TextFontSize.Large}>
                    {network.name}
                  </Text>
                </Link>
              ) : (
                <Text>-</Text>
              )}
            </Stack>
          </Stack>
          <Stack alignItems="center" justifyContent="space-between">
            <Stack alignItems="center" flex={1} gap={GapEnum.XS}>
              <Icon
                color={IconColor.Secondary}
                iconSize={IconSize.Small}
                name="map-01"
              />
              <MiniTitle color={TextColor.Secondary}>
                {t(
                  'delmonicos-shared-sdk.outputs-gateway-charge-point.location',
                )}
              </MiniTitle>
            </Stack>
            <Stack
              flex={1}
              gap={GapEnum.M}
              direction="row"
              alignItems="center"
              justifyContent="stretch">
              {location ? (
                <Link
                  onClick={() =>
                    navigate(
                      generatePath(APP_PATH.LOCATION.DETAILS, {
                        id: location.id,
                      }),
                    )
                  }>
                  <Text
                    className={css.linkUnderlined}
                    fontSize={TextFontSize.Large}>
                    {location.name}
                  </Text>
                </Link>
              ) : (
                <Text>-</Text>
              )}
            </Stack>
          </Stack>
          <Stack alignItems="flex-start" justifyContent="space-between">
            <Stack flex={1} gap={GapEnum.XS}>
              <Icon
                color={IconColor.Secondary}
                iconSize={IconSize.Small}
                name="marker-pin-01"
              />
              <MiniTitle color={TextColor.Secondary}>
                {t('delmonicos-shared-sdk.outputs-location.full-address')}
              </MiniTitle>
            </Stack>
            <Stack flex={1}>
              {locationFull ? (
                <Stack direction="column">
                  <Text
                    color={
                      TextColor.Primary
                    }>{`${locationFull.address},`}</Text>
                  <Text color={TextColor.Primary}>
                    {`${locationFull.postalCode} ${locationFull.city} - ${locationFull.country}`}
                  </Text>
                </Stack>
              ) : (
                <Text>-</Text>
              )}
            </Stack>
          </Stack>
        </Stack>
        <Stack flex={1} className={css.mapContainer}>
          {apiKey ? (
            <GeoMap
              mapId="static-map-charge-point-location"
              apiKey={apiKey}
              zoom={16}
              position={
                locationFull?.coordinates
                  ? createGoogleLatLngLiteralFromGeoLocation({
                      latitude: Number(locationFull.coordinates.latitude),
                      longitude: Number(locationFull.coordinates.longitude),
                    })
                  : undefined
              }
            />
          ) : (
            <Text color={TextColor.Error}>
              {t('global.missing-google-api-key')}
            </Text>
          )}
        </Stack>
      </Card>
      <Dialog
        onClose={() => setActionUpdateIsDialogOpen(false)}
        open={actionUpdateIsDialogOpen}
        confirmClose
        size={DialogSize.MEDIUM}>
        <Dialog.Header
          icon="map-01"
          onClose={() => setActionUpdateIsDialogOpen(false)}
          title={t('$.charge-point-location-update.title')}
        />
        <ChargePointLocationUpdateController
          onCancel={() => setActionUpdateIsDialogOpen(false)}
          onSuccess={() => setActionUpdateIsDialogOpen(false)}
        />
      </Dialog>
    </>
  );
};
